import { Cost } from '@carbon/icons-react';
import { FlowBox, HorizBox, Pad, PadBox, VertBox } from 'component/basics';
import { ReactionButton } from 'component/button';
import {
    colorBorderPrimary,
    colorBorderSelected,
    colorButtonSecondary,
    colorSurfacePrimary,
    colorSurfaceSecondary,
    colorTextDisabled,
    colorTextPrimary,
    colorTextSecondary,
} from 'component/color';
import type { CommentConfig } from 'component/comment';
import { DropdownField, FormField } from 'component/form';
import { Popup } from 'component/popup';
import { UtilityText } from 'component/text';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Path, Svg } from 'react-native-svg';
import { useCollection, useDatastore, useGlobalProperty, usePersonaKey } from 'util/datastore';
import { logEventAsync } from 'util/eventlog';
import { REPLACE, useConfig, useIsReadOnly } from 'util/features';
import type { Reaction } from 'util/stdconfig';
import { reactionTable, upvoteTable } from 'util/stdconfig';
import type { Feature, TLabel } from 'util/stdtypes';
import { t, undefS } from 'util/stdtypes';
import { toBool } from 'util/util';
import { toHoverColor } from 'util/util-color';

export type ReactionTypesConfig = {
    reactionTypes: { key: string; label: TLabel; emoji: string }[];
};

export const ReactionTypesFeature: Feature<CommentConfig, ReactionTypesConfig> = {
    name: 'Reaction Types',
    key: 'reactiontypes',
    providesConfig: {
        editScreenWidgets: [
            {
                slot: 'prosocial-format-widget',
                component: ReactionSetPicker,
            },
        ],
    },
    receivesConfigDefaults: {
        reactionTypes: [
            { key: 'Relatable', label: t('Relatable'), emoji: '❤️' },
            { key: 'Helpful', label: t('Helpful'), emoji: '➕' },
            { key: 'Controversial', label: t('Controversial'), emoji: '💬' },
            { key: 'Interesting', label: t('Interesting'), emoji: '💡' },
        ],
    },
};

export const ReactionTypesMultipleButtonsFeature: Feature<CommentConfig, {}> = {
    name: 'Multiple Buttons',
    key: 'reactiontypesmultiplebuttons',
    dev: true,
    parentFeature: 'reactiontypes',
    providesConfig: {
        commentBelowWidgets: [ReactionTypesMultipleWidget],
    },
};

export const ReactionTypesPopupButtonFeature: Feature<CommentConfig, {}> = {
    name: 'Popup Button',
    key: 'reactiontypespopupbutton',
    dev: true,
    parentFeature: 'reactiontypes',
    providesConfig: {
        commentBelowWidgets: [ReactionTypesPopupWidget],
    },
};

export const SeriousReactionsFeature: Feature<ReactionTypesConfig, {}> = {
    name: 'Serious topic',
    key: 'seriousreactions',
    parentFeature: 'reactiontypes',
    providesConfig: {
        reactionTypes: REPLACE([
            { key: 'Constructive', label: t('Constructive'), emoji: '🤝' },
            { key: 'Insightful', label: t('Insightful'), emoji: '💡' },
            { key: 'Empathetic', label: t('Empathetic'), emoji: '🫴' },
            { key: 'Nuanced', label: t('Nuanced'), emoji: '⚖️' },
        ]),
    },
};

export const PersonalReactionsFeature: Feature<ReactionTypesConfig, {}> = {
    name: 'Personal topic',
    key: 'personalreactions',
    parentFeature: 'reactiontypes',
    providesConfig: {
        reactionTypes: REPLACE([
            { key: 'Relatable', label: t('Relatable'), emoji: '❤️' },
            { key: 'Helpful', label: t('Helpful'), emoji: '➕' },
            { key: 'Interesting', label: t('Interesting'), emoji: '💡' },
            { key: 'Nuanced', label: t('Nuanced'), emoji: '⚖️' },
        ]),
    },
};

export const FunReactionsFeature: Feature<ReactionTypesConfig, {}> = {
    name: 'Fun topic',
    key: 'funreactions',
    parentFeature: 'reactiontypes',
    providesConfig: {
        reactionTypes: REPLACE([
            { key: 'Funny', label: t('Funny'), emoji: '😂' },
            { key: 'Enjoyable', label: t('Enjoyable'), emoji: '❤️' },
            { key: 'Surprising', label: t('Surprising'), emoji: '🤯 ' },
            { key: 'Playful', label: t('Playful'), emoji: '😜' },
        ]),
    },
};

export const NarwwhalReactionsFeature: Feature<ReactionTypesConfig, {}> = {
    name: 'Narwhal Reactions',
    key: 'narwhalreactions',
    dev: true,
    parentFeature: 'reactiontypes',
    providesConfig: {
        reactionTypes: REPLACE([
            { key: 'Best', label: t('Best'), emoji: '🏆', single: true },
            { key: 'Provocative', label: t('Provocative'), emoji: '🔥' },
            { key: 'New to me', label: t('New to me'), emoji: '💡' },
            { key: 'Clarifying', label: t('Clarifying'), emoji: '🧠' },
        ]),
    },
};

export const NewPublicReactionsFeature: Feature<ReactionTypesConfig, {}> = {
    name: 'Legacy reactions',
    key: 'newpublicreactions',
    parentFeature: 'reactiontypes',
    providesConfig: {},
};

export function ReactionSetPicker() {
    const datastore = useDatastore();

    const options = [
        { key: 'defaultreactions', label: t('Default Set') },
        { key: 'funreactions', label: t('Fun Space') },
        { key: 'personalreactions', label: t('Personal Space') },
        { key: 'seriousreactions', label: t('Serious Space') },
    ];
    const features = useGlobalProperty('features') ?? {};
    const enabledFeature = options.map((option) => option.key).filter((key) => features[key])[0] ?? options[0].key;
    return (
        <FormField label={t('Reaction set')} required>
            <DropdownField
                onChange={(newFeature) => {
                    const newFeatures = { ...features, [newFeature]: true, [enabledFeature]: false };
                    datastore.setGlobalProperty('features', newFeatures);
                }}
                options={options}
                placeholder={t('Default Set')}
                testID='reaction-set-picker'
                value={enabledFeature}
            />
            <Pad size={12} />
            <ReactionsPreview />
        </FormField>
    );
}

export function ReactionsPreview() {
    const s = ReactionsPreviewStyle;
    const { reactionTypes } = useConfig<ReactionTypesConfig>();
    return (
        <FlowBox columnGap={16} rowGap={12} style={s.previewBox}>
            {reactionTypes?.map((reaction, i) => (
                <HorizBox center gap={8} key={i}>
                    <UtilityText text={reaction.emoji} />
                    <UtilityText label={reaction.label} type='small' />
                </HorizBox>
            ))}
        </FlowBox>
    );
}
const ReactionsPreviewStyle = StyleSheet.create({
    previewBox: {
        borderRadius: 8,
        backgroundColor: colorSurfaceSecondary,
        paddingHorizontal: 16,
        paddingVertical: 12,
    },
});

export function IconLikePlus({ color }) {
    return (
        <Svg fill='none' height={14} viewBox='0 0 14 14' width={14}>
            <Path
                clipRule='evenodd'
                d='M12.0644 4.35228C12.275 4.44884 12.4614 4.59117 12.61 4.76891C12.7336 4.9175 12.8224 5.0918 12.87 5.27911C12.9176 5.46641 12.9228 5.66198 12.8851 5.85154C12.8052 6.24306 12.7213 6.63349 12.637 7.02505L12.6203 7.10305C12.5228 7.5562 12.6203 7.10305 12.2985 8.20466H11.4472C11.8487 7.00003 11.6949 7.43542 11.8048 6.92477C11.8144 6.88004 11.8241 6.8353 11.8337 6.79057C11.9132 6.4219 11.9928 6.05306 12.0672 5.68406C12.0807 5.61567 12.0784 5.54508 12.0605 5.4777C12.0426 5.41032 12.0095 5.34793 11.9637 5.29531C11.8926 5.21438 11.8046 5.15003 11.706 5.10679C11.6073 5.06354 11.5004 5.04247 11.3927 5.04504H9.41515C9.17613 5.04477 8.94698 4.94972 8.77793 4.78075C8.60888 4.61178 8.51373 4.38267 8.51334 4.14366V2.06934C8.51601 1.95804 8.48954 1.84797 8.43657 1.75004C8.3836 1.65211 8.30596 1.56973 8.21135 1.51104C8.08373 1.43095 7.93266 1.39668 7.78298 1.41385C7.51581 1.45702 7.40715 1.63701 7.2059 2.06872C7.17795 2.1292 7.14959 2.18989 7.12018 2.24996C6.84184 2.81894 6.52115 3.3662 6.16081 3.88713C5.97939 4.17161 5.74276 4.41682 5.46493 4.60825C5.18709 4.79968 4.87368 4.93346 4.54324 5.00166V10.9964H8.23481V11.8315H4.44376C4.36038 12.0117 4.2273 12.1643 4.06016 12.2714C3.89302 12.3786 3.69878 12.4358 3.50025 12.4363H1.64826C1.37179 12.436 1.10675 12.326 0.91126 12.1305C0.715771 11.935 0.6058 11.67 0.605469 11.3935V4.64878C0.6058 4.37232 0.715771 4.10728 0.91126 3.91179C1.10675 3.7163 1.37179 3.60633 1.64826 3.606H3.50046C3.69185 3.60683 3.87931 3.66044 4.0422 3.76093C4.2051 3.86142 4.33712 4.00489 4.42374 4.17557C4.86012 4.06812 5.23707 3.79404 5.47383 3.41204C5.81069 2.92569 6.11047 2.41467 6.37062 1.88331C6.39313 1.83743 6.41493 1.79096 6.43659 1.74476C6.44103 1.73532 6.44545 1.72588 6.44988 1.71647C6.65906 1.26702 6.92038 0.707882 7.65033 0.590255C7.86182 0.56005 8.07733 0.57577 8.2822 0.636343C8.48707 0.696917 8.67648 0.800922 8.83754 0.941279C8.99859 1.08164 9.12752 1.25505 9.21553 1.44971C9.30354 1.64437 9.34858 1.85571 9.34757 2.06934V4.14324C9.34757 4.16116 9.35469 4.17835 9.36736 4.19102C9.38004 4.20369 9.39723 4.21081 9.41515 4.21081H11.3927C11.6243 4.2074 11.8538 4.25573 12.0644 4.35228ZM1.64826 11.6021H3.50046C3.6625 11.6021 3.70901 11.4494 3.7088 11.3147V4.89822C3.7088 4.87957 3.70932 4.85905 3.70986 4.83739C3.71262 4.72747 3.71611 4.58817 3.65687 4.51552C3.63798 4.49214 3.61413 4.47326 3.58704 4.46024C3.55995 4.44721 3.5303 4.44038 3.50025 4.44023H1.64826C1.59294 4.44023 1.5399 4.4622 1.50078 4.50131C1.46167 4.54042 1.4397 4.59347 1.4397 4.64878V11.3935C1.4397 11.4488 1.46167 11.5019 1.50078 11.541C1.5399 11.5801 1.59294 11.6021 1.64826 11.6021Z'
                fill={color}
                fillRule='evenodd'
            />
            <Path
                d='M10.9221 11.9421V13.4247H11.9105V11.9421H13.3931V10.9537H11.9105V9.47107H10.9221V10.9537H9.43945V11.9421H10.9221Z'
                fill={color}
            />
            <Path
                clipRule='evenodd'
                d='M12.0644 4.35228C12.275 4.44884 12.4614 4.59117 12.61 4.76891C12.7336 4.9175 12.8224 5.0918 12.87 5.27911C12.9176 5.46641 12.9228 5.66198 12.8851 5.85154C12.8052 6.24306 12.7213 6.63349 12.637 7.02505L12.6203 7.10305C12.5228 7.5562 12.6203 7.10305 12.2985 8.20466H11.4472C11.8487 7.00003 11.6949 7.43542 11.8048 6.92477C11.8144 6.88004 11.8241 6.8353 11.8337 6.79057C11.9132 6.4219 11.9928 6.05306 12.0672 5.68406C12.0807 5.61567 12.0784 5.54508 12.0605 5.4777C12.0426 5.41032 12.0095 5.34793 11.9637 5.29531C11.8926 5.21438 11.8046 5.15003 11.706 5.10679C11.6073 5.06354 11.5004 5.04247 11.3927 5.04504H9.41515C9.17613 5.04477 8.94698 4.94972 8.77793 4.78075C8.60888 4.61178 8.51373 4.38267 8.51334 4.14366V2.06934C8.51601 1.95804 8.48954 1.84797 8.43657 1.75004C8.3836 1.65211 8.30596 1.56973 8.21135 1.51104C8.08373 1.43095 7.93266 1.39668 7.78298 1.41385C7.51581 1.45702 7.40715 1.63701 7.2059 2.06872C7.17795 2.1292 7.14959 2.18989 7.12018 2.24996C6.84184 2.81894 6.52115 3.3662 6.16081 3.88713C5.97939 4.17161 5.74276 4.41682 5.46493 4.60825C5.18709 4.79968 4.87368 4.93346 4.54324 5.00166V10.9964H8.23481V11.8315H4.44376C4.36038 12.0117 4.2273 12.1643 4.06016 12.2714C3.89302 12.3786 3.69878 12.4358 3.50025 12.4363H1.64826C1.37179 12.436 1.10675 12.326 0.91126 12.1305C0.715771 11.935 0.6058 11.67 0.605469 11.3935V4.64878C0.6058 4.37232 0.715771 4.10728 0.91126 3.91179C1.10675 3.7163 1.37179 3.60633 1.64826 3.606H3.50046C3.69185 3.60683 3.87931 3.66044 4.0422 3.76093C4.2051 3.86142 4.33712 4.00489 4.42374 4.17557C4.86012 4.06812 5.23707 3.79404 5.47383 3.41204C5.81069 2.92569 6.11047 2.41467 6.37062 1.88331C6.39313 1.83743 6.41493 1.79096 6.43659 1.74476C6.44103 1.73532 6.44545 1.72588 6.44988 1.71647C6.65906 1.26702 6.92038 0.707882 7.65033 0.590255C7.86182 0.56005 8.07733 0.57577 8.2822 0.636343C8.48707 0.696917 8.67648 0.800922 8.83754 0.941279C8.99859 1.08164 9.12752 1.25505 9.21553 1.44971C9.30354 1.64437 9.34858 1.85571 9.34757 2.06934V4.14324C9.34757 4.16116 9.35469 4.17835 9.36736 4.19102C9.38004 4.20369 9.39723 4.21081 9.41515 4.21081H11.3927C11.6243 4.2074 11.8538 4.25573 12.0644 4.35228ZM1.64826 11.6021H3.50046C3.6625 11.6021 3.70901 11.4494 3.7088 11.3147V4.89822C3.7088 4.87957 3.70932 4.85905 3.70986 4.83739C3.71262 4.72747 3.71611 4.58817 3.65687 4.51552C3.63798 4.49214 3.61413 4.47326 3.58704 4.46024C3.55995 4.44721 3.5303 4.44038 3.50025 4.44023H1.64826C1.59294 4.44023 1.5399 4.4622 1.50078 4.50131C1.46167 4.54042 1.4397 4.59347 1.4397 4.64878V11.3935C1.4397 11.4488 1.46167 11.5019 1.50078 11.541C1.5399 11.5801 1.59294 11.6021 1.64826 11.6021Z'
                fillRule='evenodd'
                stroke={color}
                strokeWidth='0.160618'
            />
            <Path
                d='M10.9221 11.9421V13.4247H11.9105V11.9421H13.3931V10.9537H11.9105V9.47107H10.9221V10.9537H9.43945V11.9421H10.9221Z'
                stroke={color}
                strokeWidth='0.160618'
            />
        </Svg>
    );
}

export function ReactionCircle({ emoji }) {
    const s = ReactionCircleStyle;
    return (
        <View style={s.circle}>
            <UtilityText aria-hidden text={emoji} type='tiny' weight='medium' />
        </View>
    );
}
const ReactionCircleStyle = StyleSheet.create({
    circle: {
        width: 24,
        height: 24,
        borderRadius: 12,
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        backgroundColor: colorButtonSecondary,
        justifyContent: 'center',
        alignItems: 'center',
    },
});

export function ReactionPopupButton({ emoji = undefS, selected = false, hover = false, disabled = false }) {
    const s = ReactionPopupButtonStyle;
    const color = disabled ? colorTextDisabled : selected ? colorTextPrimary : colorTextSecondary;
    return (
        <View style={[s.button, hover && s.hover, selected && s.selected]}>
            <IconLikePlus color={color} />
            {emoji && <ReactionCircle emoji={emoji} />}
        </View>
    );
}
const ReactionPopupButtonStyle = StyleSheet.create({
    button: {
        height: 32,
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: 6,
        paddingHorizontal: 12,
        backgroundColor: colorSurfacePrimary,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colorBorderPrimary,
    },
    hover: {
        backgroundColor: toHoverColor(colorSurfacePrimary),
    },
    selected: {
        borderColor: colorBorderSelected,
    },
});

export function ReactionPopupSelector({ onReact }) {
    const { reactionTypes } = useConfig<ReactionTypesConfig>();
    const datastore = useDatastore();

    return (
        <VertBox gap={12}>
            <UtilityText label={t('Why do you appreciate this post?')} type='small' weight='medium' />
            {reactionTypes.map((reaction, i) => (
                <ReactionButton
                    emoji={reaction.emoji}
                    key={i}
                    label={reaction.label}
                    onPress={datastore.needsLogin(() => onReact(reaction), 'react')}
                    wide
                />
            ))}
        </VertBox>
    );
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ReactionPopup({ comment }) {
    const [hover, setHover] = useState(false);
    const { reactionTypes } = useConfig<ReactionTypesConfig>();
    const personaKey = usePersonaKey();
    const myReactionList = useCollection(reactionTable, { filter: { reactTo: comment.key, from: personaKey } });
    const myUpvoteList = useCollection(upvoteTable, { filter: { comment: comment.key, from: personaKey } });
    const datastore = useDatastore();

    const myReaction = myReactionList.length > 0 ? myReactionList[0] : null;
    const myUpvote = myUpvoteList.length > 0 ? myUpvoteList[0] : null;
    const myReactionEmoji = reactionTypes.find((reaction) => reaction.label == myReaction?.reactionType)?.emoji;
    const upvotedOrReacted = myUpvote || myReaction;

    if (comment.from == personaKey) {
        return <ReactionPopupButton disabled />;
    }

    async function onReact(reaction, onClose) {
        if (!personaKey) {
            return;
        }
        datastore.addObject(reactionTable, { reactTo: comment.key, from: personaKey, reactionType: reaction.label });
        logEventAsync(datastore, 'reaction', { commentKey: comment.key, text: reaction.label });
        onClose();
    }

    function onPress() {
        if (!personaKey) return;
        if (myUpvote) {
            datastore.deleteObject(upvoteTable, myUpvote.key);
            logEventAsync(datastore, 'reaction-upvote-undo', { commentKey: comment.key });
        } else {
            datastore.addObject(upvoteTable, { comment: comment.key, from: personaKey });
            logEventAsync(datastore, 'reaction-upvote', { commentKey: comment.key });
        }
        if (myReaction) {
            datastore.deleteObject(reactionTable, myReaction.key);
            logEventAsync(datastore, 'reaction-undo', { commentKey: comment.key, text: myReaction.reactionType });
        }
    }

    function popupContent({ onClose }) {
        return (
            <ReactionPopupSelector
                onReact={(reaction) => {
                    onReact(reaction, onClose);
                }}
            />
        );
    }

    return (
        <Popup
            onPress={datastore.needsLogin(onPress, 'react')}
            popupContent={popupContent}
            popupEnabled={!upvotedOrReacted}
            setHover={setHover}
            testID='reaction-popup-button'
        >
            <ReactionPopupButton emoji={myReactionEmoji} hover={hover} selected={toBool(upvotedOrReacted)} />
        </Popup>
    );
}

export function ReactionList({ emojiList, totalCount, hover = false }) {
    return (
        <HorizBox center gap={4}>
            <HorizBox>
                {emojiList.map((emoji, i) => (
                    <View key={i} style={{ position: 'relative', marginLeft: i == 0 ? 0 : -4 }}>
                        <ReactionCircle emoji={emoji} />
                    </View>
                ))}
                {emojiList.length == 0 && <ReactionCircle emoji='👍' />}
            </HorizBox>
            <UtilityText color={colorTextSecondary} text={totalCount} type='small' underline={hover} weight='medium' />
        </HorizBox>
    );
}

export function ReactionListPopupPanel({ reactionCounts, totalCount }) {
    const { reactionTypes } = useConfig<ReactionTypesConfig>();
    const nonZeroReactions = Object.keys(reactionCounts).filter((reaction) => reactionCounts[reaction] > 0);
    const sortedReactions = reactionTypes
        .filter((reaction) => nonZeroReactions.includes(reaction.key))
        .sort((a, b) => reactionCounts[b.key] - reactionCounts[a.key]);
    return (
        <View>
            <UtilityText
                formatParams={{ count: totalCount, singular: t('appreciation'), plural: t('appreciations') }}
                label={t('{count} {noun}')}
                type='small'
                weight='medium'
            />
            <Pad size={12} />
            <VertBox gap={12}>
                {sortedReactions.map((reaction, i) => (
                    <HorizBox center gap={40} key={i} spread>
                        <UtilityText color={colorTextSecondary} text={reactionCounts[reaction.key] || '0'} type='small' />
                        <HorizBox center gap={4}>
                            <UtilityText text={reaction.emoji} type='small' />
                            <UtilityText label={reaction.label} type='small' />
                        </HorizBox>
                    </HorizBox>
                ))}
            </VertBox>
        </View>
    );
}

export function ReactionListPopup({ comment }) {
    const { reactionTypes } = useConfig<ReactionTypesConfig>();
    const [hover, setHover] = useState(false);
    const reactions = useCollection(reactionTable, { filter: { reactTo: comment.key } });
    const upvotes = useCollection(upvoteTable, { filter: { comment: comment.key } });
    const reactionCounts = countReactions(reactions);
    const reactionList = Object.keys(reactionCounts);
    const emojiList = reactionTypes.filter((reaction) => reactionList.includes(reaction.key)).map((reaction) => reaction.emoji);
    const totalReactionCount = Object.values(reactionCounts).reduce((a: number, b: number) => a + b, 0);
    const totalCount = Math.max(totalReactionCount, upvotes.length);
    if (totalCount == 0 && upvotes.length == 0) {
        return <Pad />;
    }
    if (reactionList.length == 0) {
        return <ReactionList emojiList={emojiList} totalCount={totalCount} />;
    }

    function popupContent({ onClose }) {
        return <ReactionListPopupPanel reactionCounts={reactionCounts} totalCount={totalCount} />;
    }
    return (
        <Popup popupContent={popupContent} setHover={setHover} testID='reaction-list-popup'>
            <ReactionList emojiList={emojiList} hover={hover} totalCount={totalCount} />
        </Popup>
    );
}

export function ReactionTypesPopupWidget({ comment }) {
    return (
        <PadBox top={20}>
            <HorizBox center spread>
                <ReactionPopup comment={comment} />
                <ReactionListPopup comment={comment} />
            </HorizBox>
        </PadBox>
    );
}

function countReactions(reactions: Reaction[]): Record<string, number> {
    const counts = {};
    reactions.forEach((reaction) => {
        const { reactionType } = reaction;
        counts[reactionType] = counts[reactionType] || 0;
        counts[reactionType]++;
    });
    return counts;
}

export function ReactionTypesMultipleWidget({ comment }) {
    const s = ReactionTypesWidgetsStyle;
    const { reactionTypes } = useConfig<ReactionTypesConfig>();
    const personaKey = usePersonaKey();
    const datastore = useDatastore();
    const reactions = useCollection(reactionTable, { filter: { reactTo: comment.key } });
    const myReactions = useCollection(reactionTable, { filter: { reactTo: comment.key, from: personaKey } });
    const readOnly = useIsReadOnly();

    const reactionCounts = countReactions(reactions);
    const myReactionCounts = countReactions(myReactions);
    const viewOnly = comment.from == personaKey || readOnly;

    async function onReact({ reaction }) {
        if (!personaKey) return;
        if (myReactionCounts[reaction.label]) {
            datastore.deleteObject(reactionTable, myReactions.find((r) => r.reactionType == reaction.label)?.key);
            logEventAsync(datastore, 'reaction-undo', { commentKey: comment.key, text: reaction.label });
        } else {
            if (reaction.single) {
                const existing = datastore.getCollection(reactionTable, { filter: { from: personaKey, reactionType: reaction.label } });
                existing.forEach((r) => datastore.deleteObject(reactionTable, r.key));
            }
            await datastore.addObject(reactionTable, { reactTo: comment.key, from: personaKey, reactionType: reaction.label });
            logEventAsync(datastore, 'reaction', { commentKey: comment.key, text: reaction.label });
        }
    }

    const shownReactionTypes = reactionTypes.filter((reaction) => !viewOnly || reactionCounts[reaction.key] > 0);

    return (
        <View style={s.outer}>
            {shownReactionTypes.map((reaction, i) => (
                <PadBox key={i} right={viewOnly ? 24 : 16} top={20}>
                    <ReactionButton
                        count={reactionCounts[reaction.key] || 0}
                        emoji={reaction.emoji}
                        label={reaction.label}
                        onPress={datastore.needsLogin(() => onReact({ reaction }), 'react')}
                        selected={myReactionCounts[reaction.key] > 0}
                        viewOnly={viewOnly}
                    />
                </PadBox>
            ))}
        </View>
    );
}
const ReactionTypesWidgetsStyle = StyleSheet.create({
    outer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
    },
});
